import React from 'react';
import {
    AlertCircle
} from 'lucide-react';
import ResourcePicker from '../../components/ResourcePicker';
import {
    KeyValueOelEditor, StepCommonAdvanced, StepCommonInlineFields,
    useProcedureStepIds
} from './_shared';
import {
    LlmStepZone, LlmLabeled, LlmStepCanvas
} from './llmStepLayout';

/**
 * InlineSubflowStep — kind:"subflow".
 * Runs a named flow defined under the opcode's `flows[]`. Schema:
 *   { subflow: { ref: <flow_id> }, with: { paramName: oelStr, … } }
 */
export default function InlineSubflowStep({
    step, onPatchStep, flows = [], oelContext
}) {
    const ref = step.subflow?.ref || '';
    const withMap = step.with || {};
    const stepIdOptions = useProcedureStepIds(oelContext);
    const patch = (p) => onPatchStep({ ...step, ...p });

    function setRef(newRef) {
        patch({
            subflow: {
                ...(step.subflow || {}),
                ref: newRef
            }
        });
    }
    function setWith(next) {
        patch({ with: next });
    }

    return (
        <LlmStepCanvas>
            <LlmStepZone
                variant="configuration"
                title="Flow"
                description={`Pick a flow declared on this opcode (${flows.length} declared).`}
            >
                {flows.length === 0 ? (
                    <div className="flex items-start gap-2 rounded-md border border-amber-300/50 bg-amber-50/40 px-3 py-2 text-[11.5px] text-amber-900">
                        <AlertCircle className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" />
                        <span>
                            No flows declared on this opcode yet. Add one under the
                            opcode-level <b>Flows</b> section first.
                        </span>
                    </div>
                ) : (
                    <LlmLabeled
                        label="Reference"
                        help="Which flow to run — edit the flow's steps under Flows in the left panel."
                    >
                        <ResourcePicker
                            kind="flow"
                            value={ref}
                            onChange={(id) => setRef(id)}
                            localItems={flows}
                            placeholder="No flow selected"
                        />
                    </LlmLabeled>
                )}
            </LlmStepZone>

            <LlmStepZone
                variant="arguments"
                title="With (parameters)"
                description="Flow input parameters — each value is an OEL expression."
                help="Each row maps a flow input name to an OEL expression (e.g. topic → @input.real_time_data). Names must match what the flow expects; use the @ menu to pick opcode inputs or prior step outputs."
            >
                <KeyValueOelEditor
                    value={withMap}
                    onChange={setWith}
                    keyLabel="Parameter"
                    addLabel="Add parameter"
                    placeholderKey="param_name"
                    placeholderValue="@input.field"
                    oelContext={oelContext}
                />
            </LlmStepZone>

            <StepCommonInlineFields
                step={step}
                onPatchStep={onPatchStep}
                stepIdOptions={stepIdOptions}
            />

            <StepCommonAdvanced
                step={step}
                onPatchStep={onPatchStep}
                oelContext={oelContext}
            />
        </LlmStepCanvas>
    );
}
